import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/api";
import "./stylesheet/AdminTickets.css";
import logoSvg from "../assets/logo.svg";

const STATUS_TABS = [
  { key: "Pendente", label: "Pendentes" },
  { key: "Em atendimento", label: "Em Atendimento" },
  { key: "Aguardando Peça", label: "Aguardando Peça" },
  { key: "Concluído", label: "Concluídos" },
  { key: "Cancelado", label: "Cancelados" },
];

export default function AdminTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStatus, setActiveStatus] = useState("Pendente");

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [technician, setTechnician] = useState("");
  const [status, setStatus] = useState("Em atendimento");

  // --- ESTADOS DO POPUP ANIMADO ---
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    loadTickets();
  }, []);

  async function loadTickets() {
    try {
      setLoading(true);
      const res = await api.get("tickets/listTickets.php");
      if (res.data?.success) {
        const unique = Array.from(
          new Map(res.data.data.map((t) => [t.id, t])).values()
        );
        setTickets(unique);
      }
    } catch {
      setErrorMessage("Não foi possível carregar os chamados.");
      setShowError(true);
    } finally {
      setLoading(false);
    }
  }

  const filteredTickets = useMemo(() => {
    return tickets.filter((t) => t.status === activeStatus);
  }, [tickets, activeStatus]);

  function openModal(ticket) {
    setSelectedTicket(ticket);
    setTechnician(ticket.technician || "");
    setStatus(ticket.status || "Em atendimento");
  }

  function closeModal() {
    setSelectedTicket(null);
    setTechnician("");
  }

  async function handleUpdateTicket() {
    if (!technician.trim()) {
      setErrorMessage("Informe o técnico responsável para atualizar o chamado.");
      setShowError(true);
      return;
    }
    try {
      const res = await api.post("tickets/updateTicket.php", {
        id: selectedTicket.id,
        status,
        technician,
      });
      if (res.data.success) {
        loadTickets();
        closeModal();
      } else {
        setErrorMessage(res.data.message || "Erro ao atualizar chamado.");
        setShowError(true);
      }
    } catch {
      setErrorMessage("Erro de comunicação com o servidor.");
      setShowError(true);
    }
  }

  async function removeTicket(id) {
    if (!window.confirm("Deseja realmente excluir este chamado?")) return;
    try {
      const res = await api.post("tickets/deleteTicket.php", { ticket_id: id });
      if (res.data.success) {
        loadTickets();
      } else {
        setErrorMessage("Não foi possível excluir o chamado.");
        setShowError(true);
      }
    } catch {
      setErrorMessage("Erro ao processar exclusão.");
      setShowError(true);
    }
  }

  return (
    <div className="admin-tickets-page">
      <header className="portal-header">
        <div className="header-logo-group" onClick={() => navigate("/admin/dashboard")} style={{cursor: 'pointer'}}>
          <img src={logoSvg} alt="Logo" className="portal-logo-svg" />
        </div>
        <div className="header-nav-info">
          <button className="back-link-text" onClick={() => navigate("/admin/dashboard")}>
            Voltar ao Menu
          </button>
        </div>
      </header>

      <div className="tickets-container">
        <div className="tabs-container">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`tab-btn ${activeStatus === tab.key ? "active" : ""}`}
              onClick={() => setActiveStatus(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <main className="tickets-list">
          {loading ? (
            <div className="loading-state">Carregando chamados...</div>
          ) : filteredTickets.length === 0 ? (
            <div className="empty-state">Nenhum chamado encontrado.</div>
          ) : (
            filteredTickets.map((ticket) => (
              <div key={ticket.id} className="ticket-card">
                <div className="card-accent-line"></div>
                <div className="card-content">
                  <div className="card-header-row">
                    <span className="ticket-id">#{ticket.id}</span>
                    <span className={`status-pill status-${activeStatus.toLowerCase().replace(" ", "-")}`}>
                      {activeStatus}
                    </span>
                  </div>

                  <h2 className="requester-name">{ticket.name}</h2>
                  <div className="location-row">
                    <span>📍 {ticket.sector}</span>
                    <span className="separator">•</span>
                    <span>{ticket.location}</span>
                  </div>
                  
                  <div className="urgency-badge" data-urgency={ticket.urgency.toLowerCase()}>
                    Urgência: {ticket.urgency}
                  </div>

                  <p className="ticket-desc-box">{ticket.description}</p>

                  <div className="card-footer-admin">
                    <div className="tech-assigned">
                      {ticket.technician ? (
                        <><span className="label">Técnico:</span> {ticket.technician}</>
                      ) : (
                        <span className="no-tech">Sem técnico atribuído</span>
                      )}
                    </div>
                    <div className="card-actions">
                      <button className="btn-update" onClick={() => openModal(ticket)}>Atualizar</button>
                      <button className="btn-remove" onClick={() => removeTicket(ticket.id)}>Excluir</button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </main>
      </div>

      {/* MODAL DE ATUALIZAÇÃO */}
      {selectedTicket && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2 className="modal-title">Atualizar Chamado</h2>
            <div className="form-group">
              <label>Técnico Responsável</label>
              <input
                className="modal-input"
                value={technician}
                onChange={(e) => setTechnician(e.target.value)}
                placeholder="Nome do técnico"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Alterar Status</label>
              <select className="modal-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_TABS.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
              </select>
            </div>
            <div className="modal-actions">
              <button className="confirm-btn" onClick={handleUpdateTicket}>Salvar</button>
              <button className="cancel-btn" onClick={closeModal}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* POPUP DE ERRO ANIMADO (PADRÃO DO SISTEMA) */}
      {showError && (
        <div className="error-overlay" style={{ zIndex: 9999 }}>
          <div className="error-popup-card">
            <h3>Atenção</h3>
            <p>{errorMessage}</p>
            <button className="error-close-btn" onClick={() => setShowError(false)}>
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}